import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Card,
  CardContent,
  Fade,
  LinearProgress,
  Alert,
  Chip,
  Avatar,
  Zoom
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import SweetAlert2 from "react-sweetalert2";
import { useNavigate } from "react-router-dom";
import { useGeolocated } from "react-geolocated";
import { headerData } from "../../data/headerCostum";
import { useLocalStorage } from "../../auth/useLocalStorage";
import Copyright from "./components/Copyright";

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  marginBottom: theme.spacing(2),
  overflow: 'visible',
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(29, 195, 159, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'rgba(29, 195, 159, 0.4)',
      boxShadow: '0 4px 12px rgba(29, 195, 159, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 4px 20px rgba(29, 195, 159, 0.2)',
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(29, 195, 159, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'rgba(29, 195, 159, 0.4)',
      boxShadow: '0 4px 12px rgba(29, 195, 159, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 4px 20px rgba(29, 195, 159, 0.2)',
    },
  },
}));

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
      light: '#4dd4b8',
      dark: '#14a085',
    },
    secondary: {
      main: '#8ff9ab',
      light: '#b3fac5',
      dark: '#6bc98a',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          },
        },
      },
    },
  },
});

const LaporanLongwis = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery('(max-height: 600px)');
  
  const [longwis, setLongwis] = useLocalStorage("longwis", null);
  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false,
    },
    userDecisionTimeout: 5000,
  });

  const [swalProps, setSwalProps] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    kecamatan: null,
    kelurahan: null,
    longwis: null,
    alamat: null,
    nama_pic: '',
    kontak_pic: '',
    tanggal: null,
    status: ''
  });

  const [kecamatan, setKecamatan] = useState([]);
  const [kelurahan, setKelurahan] = useState([]);
  const [data_longwis, setDataLongwis] = useState([]);
  const [data_alamat, setDataAlamat] = useState([]);
  const [list_longwis, setListLongwis] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = (event) => {
    event.preventDefault();
    if (validateForm()) {
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!data.kecamatan) newErrors.kecamatan = 'Kecamatan harus dipilih';
    if (!data.kelurahan) newErrors.kelurahan = 'Kelurahan harus dipilih';
    if (!data.longwis) newErrors.longwis = 'Nama Longwis harus dipilih';
    if (!data.alamat) newErrors.alamat = 'Alamat harus dipilih';
    if (!data.nama_pic.trim()) newErrors.nama_pic = 'Nama PIC harus diisi';
    if (!data.kontak_pic.trim()) newErrors.kontak_pic = 'Kontak PIC harus diisi';
    if (data.kontak_pic && !/^\d{10,15}$/.test(data.kontak_pic)) {
      newErrors.kontak_pic = 'Kontak PIC harus berupa nomor telepon yang valid (10-15 digit)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getLongwisData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_API_URL + "api/listlongwis", {
        method: "get",
        headers: headerData
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const res = await response.json();
      
      if (res && Array.isArray(res)) {
        setListLongwis(res);
        getKecData(res);
      } else {
        throw new Error(res?.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching longwis data:', error);
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Gagal memuat data. Silakan coba lagi.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const getKecData = useCallback((data) => {
    const kecamatanSet = new Set();
    const dataKecamatan = [];
    
    data.forEach((item, index) => {
      if (!kecamatanSet.has(item.kecamatan)) {
        kecamatanSet.add(item.kecamatan);
        dataKecamatan.push({ 
          nama: item.kecamatan, 
          id: index 
        });
      }
    });
    
    setKecamatan(dataKecamatan);
  }, []);

  const kecHandle = useCallback((selectedKecamatan) => {
    if (selectedKecamatan && list_longwis) {
      const filteredData = list_longwis.filter(item => item.kecamatan === selectedKecamatan.nama);
      const kelurahanSet = new Set();
      const dataKelurahan = [];
      
      filteredData.forEach((item, index) => {
        if (!kelurahanSet.has(item.kelurahan)) {
          kelurahanSet.add(item.kelurahan);
          dataKelurahan.push({ 
            nama: item.kelurahan, 
            id: index 
          });
        }
      });
      
      setKelurahan(dataKelurahan);
      // Reset dependent fields
      setData(prev => ({
        ...prev,
        kelurahan: null,
        longwis: null,
        alamat: null
      }));
      setDataLongwis([]);
      setDataAlamat([]);
      setErrors(prev => ({ ...prev, kelurahan: null, longwis: null, alamat: null }));
    }
  }, [list_longwis]);

  const kelHandle = useCallback((selectedKelurahan) => {
    if (selectedKelurahan && list_longwis) {
      const filteredData = list_longwis.filter(item => item.kelurahan === selectedKelurahan.nama);
      const longwisSet = new Set();
      const dataLongwis = [];
      
      filteredData.forEach((item, index) => {
        if (!longwisSet.has(item.nama_lorong)) {
          longwisSet.add(item.nama_lorong);
          dataLongwis.push({ 
            nama: item.nama_lorong, 
            id: index 
          });
        }
      });
      
      setDataLongwis(dataLongwis);
      // Reset dependent fields
      setData(prev => ({
        ...prev,
        longwis: null,
        alamat: null
      }));
      setDataAlamat([]);
      setErrors(prev => ({ ...prev, longwis: null, alamat: null }));
    }
  }, [list_longwis]);

  const longwisHandle = useCallback((selectedLongwis) => {
    if (selectedLongwis && list_longwis) {
      const filteredData = list_longwis.filter(item => item.nama_lorong === selectedLongwis.nama);
      const alamatSet = new Set();
      const dataAlamat = [];
      
      filteredData.forEach((item, index) => {
        if (!alamatSet.has(item.alamat_lorong)) {
          alamatSet.add(item.alamat_lorong);
          dataAlamat.push({ 
            nama: item.alamat_lorong, 
            id: index 
          });
        }
      });
      
      setDataAlamat(dataAlamat);
      // Reset dependent field
      setData(prev => ({ ...prev, alamat: null }));
      setErrors(prev => ({ ...prev, alamat: null }));
    }
  }, [list_longwis]);

  const sendData = async () => {
    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_API_URL + "api/longwis", {
        method: "post",
        headers: headerData,
        body: JSON.stringify({
          kecamatan: data.kecamatan.nama,
          kelurahan: data.kelurahan.nama,
          nama_longwis: data.longwis.nama,
          alamat: data.alamat.nama,
          nama_pic: data.nama_pic,
          kontak_pic: data.kontak_pic,
          tanggal: null,
          status: data.status,
          lokasi: coords ? `${coords.latitude},${coords.longitude}` : null
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const res = await response.json();
      
      if (res.nama_longwis) {
        setSwalProps({
          show: true,
          title: "Sukses",
          icon: "success",
          text: "Data telah ditambahkan successfully",
        });
        setLongwis(res);
        navigate("/pic/cctv", {
          state: {
            data_longwis: res,
          },
        });
      } else {
        throw new Error(res?.message || 'Failed to save data');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: error.message || "Gagal menyimpan data. Silakan coba lagi.",
      });
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };



  useEffect(() => {
    if (!list_longwis) {
      getLongwisData();
    }
  }, [list_longwis, getLongwisData]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg,rgb(102, 234, 102) 0%,rgb(75, 162, 87) 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="sm" sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 2, sm: 4 }
        }}>
          <CssBaseline />
          <SweetAlert2
            {...swalProps}
            didClose={() => setSwalProps({})}
          />
          
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: { xs: 0, sm: '16px' },
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                m: { xs: 0, sm: 4 },
                maxHeight: { xs: '100vh', sm: '90vh' },
                overflow: 'auto'
              }
            }}
          >
            <DialogTitle sx={{ textAlign: 'center', pb: 1, px: { xs: 2, sm: 3 } }}>
              <CheckCircleIcon color="primary" sx={{ fontSize: { xs: 40, sm: 48 }, mb: 1 }} />
              <Typography variant={{ xs: "h6", sm: "h5" }} fontWeight={600}>
                Konfirmasi Data
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', px: { xs: 2, sm: 3 } }}>
              <DialogContentText sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, color: 'text.primary' }}>
                Apakah Anda yakin bahwa data yang dimasukkan telah sesuai?
              </DialogContentText>
              <Box sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" align="left" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  <strong>Kecamatan:</strong> {data.kecamatan?.nama}<br/>
                  <strong>Kelurahan:</strong> {data.kelurahan?.nama}<br/>
                  <strong>Nama Longwis:</strong> {data.longwis?.nama}<br/>
                  <strong>Alamat:</strong> {data.alamat?.nama}<br/>
                  <strong>Nama PIC:</strong> {data.nama_pic}<br/>
                  <strong>Kontak PIC:</strong> {data.kontak_pic}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', p: { xs: 2, sm: 3 } }}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                sx={{ minWidth: { xs: 100, sm: 120 } }}
                disabled={loading}
              >
                Kembali
              </Button>
              <Button
                variant="contained"
                onClick={sendData}
                sx={{ minWidth: { xs: 100, sm: 120 } }}
                disabled={loading}
                startIcon={loading ? <LinearProgress size={20} /> : <CheckCircleIcon />}
              >
                {loading ? 'Menyimpan...' : 'Konfirmasi'}
              </Button>
            </DialogActions>
          </Dialog>

          <Fade in timeout={800}>
            <StyledCard sx={{ 
              maxHeight: isVerySmall ? 'calc(100vh - 40px)' : { xs: 'calc(100vh - 80px)', sm: 'none' },
              overflow: { xs: 'auto', sm: 'visible' },
              my: { xs: 1, sm: 2 }
            }}>
              <CardContent sx={{ 
                p: isVerySmall ? 1 : { xs: 1.5, sm: 4 }, 
                pb: isVerySmall ? 1.5 : { xs: 2, sm: 4 } 
              }}>
                <Box textAlign="center" mb={isVerySmall ? 1 : { xs: 2, sm: 4 }}>
                  <Avatar
                    sx={{
                      width: { xs: 60, sm: 80 },
                      height: { xs: 60, sm: 80 },
                      mx: 'auto',
                      mb: { xs: 1, sm: 2 },
                      bgcolor: 'primary.main',
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
                  </Avatar>
                  <Typography 
                    variant={{ xs: "h5", sm: "h4" }}
                    fontWeight={700} 
                    color="primary.main"
                    sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                  >
                    Daftar Longwis
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Lengkapi informasi lorong wisata
                  </Typography>
                </Box>

                {loading && (
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress />
                  </Box>
                )}

                <Box component="form" onSubmit={handleOpenDialog} noValidate>
                  <Zoom in timeout={1000}>
                    <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" color="primary" />
                        Kecamatan
                      </Typography>
                      <StyledAutocomplete
                        options={kecamatan}
                        value={data.kecamatan}
                        getOptionLabel={(option) => option?.nama || ''}
                        onChange={(event, newValue) => {
                          setData({ ...data, kecamatan: newValue });
                          kecHandle(newValue);
                          setErrors(prev => ({ ...prev, kecamatan: null }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Pilih Kecamatan"
                            error={!!errors.kecamatan}
                            helperText={errors.kecamatan}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                            }}
                          />
                        )}
                        loading={loading}
                        disabled={loading}
                      />
                    </Box>
                  </Zoom>

                  <Zoom in timeout={1200}>
                    <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HomeIcon fontSize="small" color="primary" />
                        Kelurahan
                      </Typography>
                      <StyledAutocomplete
                        options={kelurahan}
                        value={data.kelurahan}
                        getOptionLabel={(option) => option?.nama || ''}
                        onChange={(event, newValue) => {
                          setData({ ...data, kelurahan: newValue });
                          kelHandle(newValue);
                          setErrors(prev => ({ ...prev, kelurahan: null }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Pilih Kelurahan"
                            error={!!errors.kelurahan}
                            helperText={errors.kelurahan}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <HomeIcon color="action" sx={{ mr: 1 }} />,
                            }}
                          />
                        )}
                        disabled={!data.kecamatan || loading}
                      />
                    </Box>
                  </Zoom>

                  <Zoom in timeout={1400}>
                    <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon fontSize="small" color="primary" />
                        Nama Lorong Wisata
                      </Typography>
                      <StyledAutocomplete
                        options={data_longwis}
                        value={data.longwis}
                        getOptionLabel={(option) => option?.nama || ''}
                        onChange={(event, newValue) => {
                          setData({ ...data, longwis: newValue });
                          longwisHandle(newValue);
                          setErrors(prev => ({ ...prev, longwis: null }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Pilih Nama Lorong Wisata"
                            error={!!errors.longwis}
                            helperText={errors.longwis}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <LocationOnIcon color="action" sx={{ mr: 1 }} />,
                            }}
                          />
                        )}
                        disabled={!data.kelurahan || loading}
                      />
                    </Box>
                  </Zoom>

                  <Zoom in timeout={1600}>
                    <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Alamat Lorong Wisata
                      </Typography>
                      <StyledAutocomplete
                        options={data_alamat}
                        value={data.alamat}
                        getOptionLabel={(option) => option?.nama || ''}
                        onChange={(event, newValue) => {
                          setData({ ...data, alamat: newValue });
                          setErrors(prev => ({ ...prev, alamat: null }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Pilih Alamat Lorong Wisata"
                            error={!!errors.alamat}
                            helperText={errors.alamat}
                          />
                        )}
                        disabled={!data.longwis || loading}
                      />
                    </Box>
                  </Zoom>

                  <Zoom in timeout={1800}>
                    <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        Nama PIC
                      </Typography>
                      <StyledTextField
                        fullWidth
                        placeholder="Masukkan Nama PIC"
                        value={data.nama_pic}
                        onChange={(event) => {
                          setData({ ...data, nama_pic: event.target.value });
                          setErrors(prev => ({ ...prev, nama_pic: null }));
                        }}
                        error={!!errors.nama_pic}
                        helperText={errors.nama_pic}
                        InputProps={{
                          startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                        }}
                        disabled={loading}
                      />
                    </Box>
                  </Zoom>

                  <Zoom in timeout={2000}>
                    <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="primary" />
                        Kontak PIC
                      </Typography>
                      <StyledTextField
                        fullWidth
                        type="tel"
                        placeholder="Masukkan Nomor Telepon"
                        value={data.kontak_pic}
                        onChange={(event) => {
                          const value = event.target.value.replace(/\D/g, '');
                          setData({ ...data, kontak_pic: value });
                          setErrors(prev => ({ ...prev, kontak_pic: null }));
                        }}
                        error={!!errors.kontak_pic}
                        helperText={errors.kontak_pic || 'Format: 081234567890'}
                        InputProps={{
                          startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                        }}
                        disabled={loading}
                      />
                    </Box>
                  </Zoom>

                  {!isGeolocationAvailable && (
                    <Alert severity="warning" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Geolocation tidak tersedia di browser ini
                    </Alert>
                  )}

                  {isGeolocationAvailable && !isGeolocationEnabled && (
                    <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Mohon aktifkan lokasi untuk akurasi data
                    </Alert>
                  )}

                  {coords && (
                    <Alert severity="success" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOnIcon fontSize="small" />
                        <Typography variant="body2">
                          Lokasi: {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  <Zoom in timeout={2200}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        background: 'linear-gradient(135deg,rgb(102, 234, 102) 0%,rgb(78, 162, 75) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg,rgb(90, 216, 90) 0%,rgb(63, 160, 76) 100%)',
                        },
                      }}
                    >
                      {loading ? 'Memproses...' : 'Lanjutkan'}
                    </Button>
                  </Zoom>
                </Box>
              </CardContent>
            </StyledCard>
          </Fade>

          <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3 } }}>
            <Copyright />
          </Box>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default LaporanLongwis;
